import { Controller } from "@hotwired/stimulus"
import consumer from "channels/consumer"
import { logger } from "../logger"

// Handles real-time attachment validation status updates via ActionCable
// Replaces: app/assets/javascripts/channels/attachment_validation_channel.js
export default class extends Controller {
  static targets = ["container", "status"]
  static values = {
    attachmentId: Number
  }

  connect() {
    // Track validation state - starts as validating (not closeable)
    this.validationComplete = false
    this.statusReceived = false // Track if we've received any status update
    this.validationFailed = false // Track if validation has failed (so we don't auto-close modal on removal)
    this.feedbackReportGenerated = false // Track if user generated a feedback report (for cleanup on close)

    // Get the modal element
    this.modalEl = this.element.closest('.modal')

    // Listen for feedback report generated events (on modal since button is in footer)
    this.feedbackReportHandler = (event) => {
      this.feedbackReportGenerated = true
      logger.log('Feedback report generated, will skip attachment cleanup on modal close')
    }
    // Listen on modal element since the feedback button is in the footer (sibling of modal-body)
    if (this.modalEl) {
      this.modalEl.addEventListener('feedback-report:generated', this.feedbackReportHandler)
    }
    
    // Check if validation is already complete based on initial DOM state
    // If the container shows success/error state, validation is already done
    if (this.hasContainerTarget) {
      const container = this.containerTarget
      const hasSuccessIcon = container.querySelector('.success-checkmark')
      const hasErrors = container.querySelector('.alert-danger, .text-bg-danger')
      
      if (hasSuccessIcon || hasErrors) {
        // Validation already complete - enable closing immediately
        this.validationComplete = true
        this.statusReceived = true
        
        // If errors are present, mark validation as failed (so removal won't auto-close modal)
        if (hasErrors) {
          this.validationFailed = true
        }
        
        // Add footer if not already present
        const modalContent = this.element.closest('.modal-content')
        if (modalContent) {
          const existingFooter = modalContent.querySelector('.modal-footer')
          if (!existingFooter) {
            // Get footer from data attribute
            const footerHTML = hasSuccessIcon 
              ? container.dataset.validationDoneFooter 
              : container.dataset.validationFailedFooter
            
            if (footerHTML) {
              modalContent.insertAdjacentHTML('beforeend', footerHTML)
            }
          }
        }
        
        // Enable closing
        this.enableModalClosing()
        
        // Don't subscribe if validation is already complete
        // But still subscribe in case status changes (e.g., processing starts)
        // Actually, let's still subscribe to handle any status updates
      }
    }
    
    // Find the modal element
    const modalEl = this.element.closest('.modal')
    if (modalEl) {
      // Prevent closing during validation (only if not already complete)
      this.preventCloseHandler = (event) => {
        if (!this.validationComplete) {
          event.preventDefault()
          event.stopPropagation()
          return false
        }
      }
      
      // Only add prevent-close handler if validation is not complete
      if (!this.validationComplete) {
        // Prevent ESC key, backdrop click, and close button during validation
        modalEl.addEventListener('hide.bs.modal', this.preventCloseHandler)
        
        // Also prevent close button clicks
        const closeButton = modalEl.querySelector('.validation-close-button')
        if (closeButton) {
          this.closeButtonHandler = (event) => {
            if (!this.validationComplete) {
              event.preventDefault()
              event.stopPropagation()
              return false
            }
          }
          closeButton.addEventListener('click', this.closeButtonHandler)
        }
      } else {
        // Validation already complete - show close button
        const closeButton = modalEl.querySelector('.validation-close-button')
        if (closeButton) {
          closeButton.style.display = ''
        }
      }
    }
    
    if (this.hasAttachmentIdValue) {
      // Find and remove any existing AttachmentValidationChannel subscription for this attachment
      // (don't remove AttachmentProcessChannel or other channel subscriptions!)
      const existingSubscriptions = consumer.subscriptions.subscriptions.filter(sub => {
        try {
          const identifier = JSON.parse(sub.identifier)
          return identifier.channel === 'AttachmentValidationChannel' &&
                 identifier.attachment_id === this.attachmentIdValue
        } catch (e) {
          return false
        }
      })

      if (existingSubscriptions.length > 0) {
        existingSubscriptions.forEach(sub => consumer.subscriptions.remove(sub))
      }

      this.subscription = consumer.subscriptions.create(
        {
          channel: "AttachmentValidationChannel",
          attachment_id: this.attachmentIdValue
        },
        {
          connected: this.connected.bind(this),
          disconnected: this.disconnected.bind(this),
          received: this.received.bind(this)
        }
      )
      
      // Fallback: If no status received within 500ms, check current status
      // This handles race conditions where validation completes before subscription callback fires
      this.statusCheckTimeout = setTimeout(() => {
        if (!this.statusReceived) {
          this.checkCurrentStatus()
        }
      }, 500)
    }
  }

  disconnect() {
    // Clear timeout if still pending
    if (this.statusCheckTimeout) {
      clearTimeout(this.statusCheckTimeout)
      this.statusCheckTimeout = null
    }

    // Remove event listeners
    const modalEl = this.element.closest('.modal')
    if (modalEl && this.preventCloseHandler) {
      modalEl.removeEventListener('hide.bs.modal', this.preventCloseHandler)
    }

    if (modalEl && this.modalHiddenHandler) {
      modalEl.removeEventListener('hidden.bs.modal', this.modalHiddenHandler)
    }

    const closeButton = modalEl?.querySelector('.validation-close-button')
    if (closeButton && this.closeButtonHandler) {
      closeButton.removeEventListener('click', this.closeButtonHandler)
    }

    // Remove feedback report event listener (from modal element)
    if (this.feedbackReportHandler && this.modalEl) {
      this.modalEl.removeEventListener('feedback-report:generated', this.feedbackReportHandler)
    }

    // Remove beforeunload handler
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }

    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription)
      this.subscription = null
    }
  }

  connected() {
    // ActionCable connection established
    // Server should broadcast current status immediately if validation is already complete
    // The timeout in connect() will check if we haven't received status after 500ms
  }
  
  async checkCurrentStatus() {
    // Fallback: If no status received within timeout, manually trigger status check
    // This handles edge cases where broadcast happens before subscription callback fires
    if (!this.hasAttachmentIdValue || this.statusReceived) {
      return
    }
    
    try {
      // The server broadcasts status on subscription, but if we haven't received it,
      // we can try to manually trigger a re-broadcast by checking the subscription
      // Actually, the simplest approach is to wait a bit more - the server broadcast
      // might just be delayed. If still nothing, we'll show an error or retry.
      
      // For now, just log - the server-side fix (removed 200ms threshold) should handle this
      logger.warn('No validation status received after timeout. Validation may have completed before subscription.')
      
      // The modal will continue showing "checking..." - user can refresh or wait
      // In most cases, the server-side broadcast should have already happened
    } catch (error) {
      logger.error('Error checking current status:', error)
    }
  }

  disconnected() {
    // ActionCable connection closed
  }

  received(data) {
    // Mark that we've received a status update
    this.statusReceived = true
    
    // Clear the timeout check since we've received status
    if (this.statusCheckTimeout) {
      clearTimeout(this.statusCheckTimeout)
      this.statusCheckTimeout = null
    }
    
    // Handle removal message (attachment destroyed due to validation failure)
    if (data.status === 'removed' && data.dom_id) {
      this.handleRemoval(data.dom_id)
      return
    }
    
    if (!this.hasContainerTarget) {
      return
    }

    // Find modal content (parent)
    const modalContent = this.element.closest('.modal-content')

    if (data.status === 'validation_done') {
      // Update container with success HTML
      this.containerTarget.innerHTML = data.doneHTML

      // Get footer HTML from data attribute
      const doneFooter = this.containerTarget.dataset.validationDoneFooter
      if (modalContent && doneFooter) {
        // Find the modal element
        const modalEl = modalContent.closest('.modal')
        
        // Ensure modal is properly shown and aria-hidden is false before inserting footer
        // This prevents accessibility warnings about focused elements in hidden modals
        if (modalEl) {
          modalEl.setAttribute('aria-hidden', 'false')
          // Ensure show class is present
          if (!modalEl.classList.contains('show')) {
            modalEl.classList.add('show')
          }
        }

        // Remove any existing modal-footer before adding new one
        const existingFooter = modalContent.querySelector('.modal-footer')
        if (existingFooter) {
          existingFooter.remove()
        }

        modalContent.insertAdjacentHTML('beforeend', doneFooter)
      }

      // Enable closing the modal (validation is complete)
      this.enableModalClosing()

      // Refresh attachment display (attachment may have been updated)
      this.refreshAttachmentDisplay()
    } else if (data.status === 'validation_failed') {
      // Mark that validation has failed - don't auto-close modal on removal
      this.validationFailed = true

      // Update container with errors HTML
      this.containerTarget.innerHTML = data.errorsHTML

      // Get footer HTML from data attribute
      let failedFooter = this.containerTarget.dataset.validationFailedFooter
      if (modalContent && failedFooter) {
        // Check if feedback data is available BEFORE inserting footer
        // This eliminates lag - button appears immediately with footer
        const feedbackData = this.containerTarget.querySelector('.feedback-report-data')
        if (feedbackData) {
          const sourceId = feedbackData.dataset.feedbackSourceId
          if (sourceId) {
            // Modify footer HTML to show button and include source_id
            failedFooter = failedFooter
              .replace('style="display: none;"', '')
              .replace('data-controller="feedback-report"',
                `data-controller="feedback-report" data-feedback-report-source-id-value="${sourceId}"`)
          }
        }

        // Find the modal element
        const modalEl = modalContent.closest('.modal')

        // Ensure modal is properly shown and aria-hidden is false before inserting footer
        // This prevents accessibility warnings about focused elements in hidden modals
        if (modalEl) {
          modalEl.setAttribute('aria-hidden', 'false')
          // Ensure show class is present
          if (!modalEl.classList.contains('show')) {
            modalEl.classList.add('show')
          }
        }

        // Remove any existing modal-footer before adding new one
        const existingFooter = modalContent.querySelector('.modal-footer')
        if (existingFooter) {
          existingFooter.remove()
        }

        modalContent.insertAdjacentHTML('beforeend', failedFooter)
      }

      // Enable closing the modal (validation failed, user can close)
      this.enableModalClosing()

      // Refresh attachment display (attachment will be destroyed or show errors)
      this.refreshAttachmentDisplay()
    } else {
      // Update status message for in-progress
      if (this.hasStatusTarget) {
        this.statusTarget.innerHTML = data.message
      }
    }
  }

  enableModalClosing() {
    // Mark validation as complete - modal can now be closed
    this.validationComplete = true

    // Find the modal element
    const modalEl = this.element.closest('.modal')
    if (!modalEl) {
      return
    }

    // Show the close button (X)
    const closeButton = modalEl.querySelector('.validation-close-button')
    if (closeButton) {
      closeButton.style.display = ''
    }

    // Remove the prevent-close handler - modal can now be closed
    if (this.preventCloseHandler) {
      modalEl.removeEventListener('hide.bs.modal', this.preventCloseHandler)
    }

    if (this.closeButtonHandler) {
      if (closeButton) {
        closeButton.removeEventListener('click', this.closeButtonHandler)
      }
    }

    // Add cleanup handler for when modal is closed (only for failed validations in feedback mode)
    // This cleans up the FeedbackReportSource if user dismisses without generating a feedback report
    if (this.validationFailed) {
      this.modalHiddenHandler = () => {
        this.cleanupFailedAttachmentIfNeeded()
      }
      modalEl.addEventListener('hidden.bs.modal', this.modalHiddenHandler, { once: true })
    }

    // Enable keyboard closing by updating modal config
    // Bootstrap doesn't allow changing config after creation, so we need to recreate the modal
    // But that's complex, so instead we'll just allow the events to proceed
    // The event handlers are removed above, so closing will work now
  }

  // Clean up FeedbackReportSource if user closes modal without generating feedback report
  async cleanupFailedAttachmentIfNeeded() {
    // Check if feedback data exists (hidden element with source_id in the content)
    const feedbackData = this.element.querySelector('.feedback-report-data')
    if (!feedbackData) {
      // No feedback data - nothing to clean up
      return
    }

    // Get the source ID from the data element
    const sourceId = feedbackData.dataset.feedbackSourceId
    if (!sourceId) {
      return
    }

    if (this.feedbackReportGenerated) {
      // User generated feedback report - cleanup will happen after download
      logger.log('Feedback report was generated, skipping source cleanup on modal close')
      return
    }

    // User closed modal without generating feedback report - clean up the FeedbackReportSource
    logger.log('Cleaning up FeedbackReportSource after modal close (no feedback report generated)')
    try {
      await fetch(`/feedback_report_sources/${sourceId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
    } catch (error) {
      // Ignore cleanup errors - source may have already been destroyed or expired
      logger.warn('Failed to cleanup FeedbackReportSource:', error)
    }
  }

  handleRemoval(domId) {
    // Find the attachment display element by dom_id
    const attachmentElement = document.getElementById(domId)
    if (!attachmentElement) {
      logger.warn('Attachment element not found for removal:', domId)
      return
    }

    // Find the attachment card container
    const cardElement = attachmentElement.closest('.attachment-item-card')
    if (!cardElement) {
      logger.warn('Attachment card not found for removal:', domId)
      return
    }

    // Extract attachment_type_file_id from card ID (format: atf_123)
    const cardId = cardElement.id
    const attachmentTypeFileIdMatch = cardId.match(/^atf_(\d+)$/)
    if (!attachmentTypeFileIdMatch) {
      logger.warn('Could not extract attachment_type_file_id from card ID:', cardId)
      // Fallback to "not yet uploaded" state
      attachmentElement.outerHTML = `
        <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
          <i class="fas fa-question-circle"></i>
          טרם הועלה
        </div>
      `
      return
    }

    const attachmentTypeFileId = attachmentTypeFileIdMatch[1]

    // Find the form to get the base URL for fetching attachments
    const form = cardElement.querySelector('form.upload-form')
    if (!form) {
      logger.warn('Upload form not found in card')
      // Fallback to "not yet uploaded" state
      attachmentElement.outerHTML = `
        <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
          <i class="fas fa-question-circle"></i>
          טרם הועלה
        </div>
      `
      return
    }

    // Get the base URL from the form action (e.g., /admin/years/1/attachments)
    const formAction = form.action
    if (!formAction) {
      logger.warn('Form action not found')
      // Fallback to "not yet uploaded" state
      attachmentElement.outerHTML = `
        <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
          <i class="fas fa-question-circle"></i>
          טרם הועלה
        </div>
      `
      return
    }

    // Extract the base path (remove /create from the end)
    const basePath = formAction.replace(/\/create$/, '')

    // Fetch the current attachments to restore the previous one (if it exists)
    // Use the index endpoint which returns the full attachment list with the card
    // Note: The versions endpoint returns a modal, not the card we need
    fetch(basePath, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      if (!html) {
        logger.warn('Empty response when fetching attachment versions')
        // Fallback to "not yet uploaded" state
        if (attachmentElement.isConnected && attachmentElement.parentNode) {
          attachmentElement.outerHTML = `
            <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
              <i class="fas fa-question-circle"></i>
              טרם הועלה
            </div>
          `
        }
        return
      }

      // Parse the HTML response
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      // The index endpoint returns the full attachment list with cards
      // Find the card and extract the attachment element from within it
      const responseCard = doc.querySelector(`#atf_${attachmentTypeFileId}`)
      if (responseCard) {
        // Find the attachment element within the response card
        const updatedElement = responseCard.querySelector(`#${domId}`)
        if (updatedElement && attachmentElement.isConnected && attachmentElement.parentNode) {
          // Restore the previous attachment (or "not yet uploaded" if none exists)
          attachmentElement.outerHTML = updatedElement.outerHTML
        } else {
          // If element not found in response card, show "not yet uploaded"
          if (attachmentElement.isConnected && attachmentElement.parentNode) {
            attachmentElement.outerHTML = `
              <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
                <i class="fas fa-question-circle"></i>
                טרם הועלה
              </div>
            `
          }
        }
      } else {
        // If card not found in response, show "not yet uploaded"
        if (attachmentElement.isConnected && attachmentElement.parentNode) {
          attachmentElement.outerHTML = `
            <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
              <i class="fas fa-question-circle"></i>
              טרם הועלה
            </div>
          `
        }
      }
    })
    .catch(error => {
      logger.error('Error fetching attachment versions:', error)
      // Fallback to "not yet uploaded" state on error
      if (attachmentElement.isConnected && attachmentElement.parentNode) {
        attachmentElement.outerHTML = `
          <div class="d-flex align-items-center gap-2 py-2 text-muted ms-2" id="${domId}">
            <i class="fas fa-question-circle"></i>
            טרם הועלה
          </div>
        `
      }
    })

    // Only close the validation modal if validation did NOT fail
    // If validation failed, keep the modal open so user can read the errors
    if (!this.validationFailed) {
      const modalEl = this.element.closest('.modal')
      if (modalEl) {
        // Use Bootstrap's modal API to close
        const bsModal = bootstrap.Modal.getInstance(modalEl)
        if (bsModal) {
          bsModal.hide()
        }
      }
    }
  }

  refreshAttachmentDisplay() {
    // Get dom_id from data attribute
    const domId = this.containerTarget.dataset.validationDoneDomId
    if (!domId) {
      return
    }

    // Find the attachment display element
    const attachmentElement = document.getElementById(domId)
    if (!attachmentElement) {
      return
    }

    // Find the attachment card container to get the form URL
    const cardElement = attachmentElement.closest('.attachment-item-card')
    if (!cardElement) {
      return
    }

    // Find the form to get the attachment refresh URL
    const form = cardElement.querySelector('form.upload-form')
    if (!form) {
      return
    }

    // Get the base URL from the form action (e.g., /admin/years/1/attachments)
    const formAction = form.action
    if (!formAction) {
      return
    }

    // Extract the base path (remove /create from the end)
    const basePath = formAction.replace(/\/create$/, '')
    
    // Fetch the updated attachment display
    // The index action returns HTML, so we'll parse it to find the updated element
    // Note: This is a fallback - the primary update mechanism is ActionCable broadcast_removal
    fetch(basePath, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      if (!html) {
        logger.warn('Empty response when refreshing attachment display')
        return
      }

      // Check if attachment element is still in the DOM before modifying
      // It might have been removed by a Turbo Stream replace or modal close
      if (!attachmentElement.isConnected || !attachmentElement.parentNode) {
        logger.log('Attachment element no longer in DOM, skipping refresh')
        return
      }

      // Parse the HTML to find the updated attachment display
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      
      // Find the updated attachment element with the same dom_id
      const updatedElement = doc.getElementById(domId)
      if (updatedElement) {
        // Double-check element is still connected before modifying
        if (!attachmentElement.isConnected || !attachmentElement.parentNode) {
          logger.log('Attachment element removed during fetch, skipping update')
          return
        }
        // Replace the attachment display with the updated version
        attachmentElement.outerHTML = updatedElement.outerHTML
      } else {
        // If attachment was destroyed, find the card and check for "not yet uploaded" state
        const cardId = cardElement.id
        const cardInResponse = doc.getElementById(cardId)
        if (cardInResponse) {
          // Find the attachment display area in the response (should show "not yet uploaded")
          const attachmentArea = cardInResponse.querySelector(`#${domId}`)
          if (attachmentArea) {
            // Double-check element is still connected before modifying
            if (!attachmentElement.isConnected || !attachmentElement.parentNode) {
              logger.log('Attachment element removed during fetch, skipping update')
              return
            }
            attachmentElement.outerHTML = attachmentArea.outerHTML
          } else {
            // If the element doesn't exist, the attachment was destroyed
            // The attachment partial will render "not yet uploaded" state when attachment is nil
            // We'll let the server response handle this, but if we can't find it, 
            // we'll remove the waiting status by clearing the element
            // The form will still be there for re-upload
            // Double-check element is still connected before modifying
            if (attachmentElement.isConnected && attachmentElement.parentNode) {
              attachmentElement.innerHTML = ''
            }
          }
        }
      }
    })
    .catch(error => {
      logger.error('Error refreshing attachment display:', error)
      // Don't show user-facing error - ActionCable broadcast_removal should handle this
      // This fetch is just a fallback
    })
  }
}

